/**
 * Leave Request Detail Page - Shows details of a specific leave request
 */

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { leaveApi } from '../api/leaveApi'
import LoadingPage from '@/shared/components/LoadingPage'
import { useAuth } from '@/core/auth/AuthContext'

const LeaveRequestDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')

  // All hooks must be called before any conditional returns
  const { data, isLoading, isPending, error } = useQuery({
    queryKey: ['leaveRequest', id],
    queryFn: () => leaveApi.getLeaveRequest(id!),
    enabled: !!id,
  })

  const cancelMutation = useMutation({
    mutationFn: () => leaveApi.cancelLeaveRequest(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] })
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      toast.success('Leave request cancelled')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to cancel leave request'
      toast.error(message)
    },
  })

  const approveMutation = useMutation({
    mutationFn: (comments?: string) => leaveApi.approveLeaveRequest(id!, { comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] })
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] })
      toast.success('Leave request approved')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to approve leave request'
      toast.error(message)
    },
  })

  const rejectMutation = useMutation({
    mutationFn: (comments?: string) => leaveApi.rejectLeaveRequest(id!, { comments }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaveRequest', id] })
      queryClient.invalidateQueries({ queryKey: ['leaveRequests'] })
      queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] })
      toast.success('Leave request rejected')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to reject leave request'
      toast.error(message)
    },
  })

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'approved':
        return {
          bg: 'bg-emerald-50',
          text: 'text-emerald-700',
          border: 'border-emerald-200',
          label: 'Approved',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
        }
      case 'pending':
        return {
          bg: 'bg-amber-50',
          text: 'text-amber-700',
          border: 'border-amber-200',
          label: 'Pending Approval',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        }
      case 'rejected':
        return {
          bg: 'bg-red-50',
          text: 'text-red-700',
          border: 'border-red-200',
          label: 'Rejected',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
        }
      case 'cancelled':
        return {
          bg: 'bg-gray-50',
          text: 'text-gray-700',
          border: 'border-gray-200',
          label: 'Cancelled',
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          ),
        }
      default:
        return {
          bg: 'bg-gray-50',
          text: 'text-gray-700',
          border: 'border-gray-200',
          label: status,
          icon: null,
        }
    }
  }

  // Show loading while query is pending or id is not available yet
  if (!id || isLoading || isPending) {
    return <LoadingPage />
  }

  if (error || !data?.data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 p-6">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Leave request not found</h3>
            <p className="text-gray-500 mb-6">The requested leave request does not exist or you don't have permission to view it.</p>
            <Link
              to="/leave/requests"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Requests
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const request = data.data
  const statusConfig = getStatusConfig(request.status)
  const isOwner = user?.id === request.user_id

  // Check if current user is the direct manager of the requester
  const isDirectManager = user?.id === request.user?.manager_id

  // Allow self-approval only if the requester has no manager (top-level users)
  const isSelfApprovalAllowed = isOwner && !request.user?.manager_id

  // Can approve if pending AND (direct manager OR self-approval allowed)
  const canApprove = request.status === 'pending' && (isDirectManager || isSelfApprovalAllowed)

  // For cancellation purposes, only the direct manager (not any approver role) can cancel
  const isApprover = isDirectManager

  // Check if leave hasn't started yet (for canceling approved leave)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startDate = new Date(request.start_date)
  startDate.setHours(0, 0, 0, 0)
  const leaveNotStarted = today < startDate

  // Owner can cancel pending requests, or approved requests before start date
  // Approvers can also cancel approved requests before start date
  const canCancelPending = isOwner && request.status === 'pending'
  const canCancelApproved = request.status === 'approved' && leaveNotStarted && (isOwner || isApprover)
  const canCancel = canCancelPending || canCancelApproved

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-2">
            <Link to="/leave" className="hover:text-white transition-colors">Leave</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/leave/requests" className="hover:text-white transition-colors">My Requests</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">Details</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Leave Request Details</h1>
              <p className="text-red-200">{request.leave_type?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/leave/requests"
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-medium rounded-xl hover:bg-white/20 transition-all border border-white/20"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-6">
        {/* Status Card */}
        <div className={`rounded-2xl shadow-sm border p-6 mb-6 ${statusConfig.bg} ${statusConfig.border}`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${statusConfig.text} bg-white/50`}>
              {statusConfig.icon}
            </div>
            <div>
              <p className={`font-semibold text-lg ${statusConfig.text}`}>{statusConfig.label}</p>
              <p className="text-sm text-gray-600">
                Submitted on {format(new Date(request.created_at), 'MMMM dd, yyyy \'at\' h:mm a')}
              </p>
            </div>
          </div>
        </div>

        {/* Main Details Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Request Information</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Leave Type */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Leave Type</label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${request.leave_type?.color}20` }}
                  >
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: request.leave_type?.color }}
                    />
                  </div>
                  <span className="font-semibold text-gray-900">{request.leave_type?.name}</span>
                </div>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Duration</label>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-3 py-1.5 rounded-lg bg-red-50 text-red-700 font-semibold">
                    {request.total_days} {request.total_days === 1 ? 'day' : 'days'}
                  </span>
                </div>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Start Date</label>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-gray-900">
                    {format(new Date(request.start_date), 'EEEE, MMMM dd, yyyy')}
                  </span>
                </div>
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">End Date</label>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium text-gray-900">
                    {format(new Date(request.end_date), 'EEEE, MMMM dd, yyyy')}
                  </span>
                </div>
              </div>
            </div>

            {/* Reason */}
            {request.reason && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-500 mb-2">Reason</label>
                <p className="text-gray-900 bg-gray-50 rounded-xl p-4">{request.reason}</p>
              </div>
            )}
          </div>
        </div>

        {/* Rejection Reason Card - Show when request is rejected */}
        {request.status === 'rejected' && request.rejection_reason && (
          <div className="bg-white rounded-2xl shadow-sm border border-red-100 overflow-hidden mb-6">
            <div className="p-6 border-b border-red-100 bg-red-50">
              <h2 className="text-lg font-semibold text-red-700">Rejection Reason</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-900 mb-4">{request.rejection_reason}</p>
              {request.rejecter && (
                <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white font-bold">
                    {request.rejecter.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Rejected by {request.rejecter.full_name}</p>
                    {request.rejected_at && (
                      <p className="text-xs text-gray-500">
                        on {format(new Date(request.rejected_at), 'MMMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Approval Info Card - Show when request is approved */}
        {request.status === 'approved' && request.approver && (
          <div className="bg-white rounded-2xl shadow-sm border border-emerald-100 overflow-hidden mb-6">
            <div className="p-6 border-b border-emerald-100 bg-emerald-50">
              <h2 className="text-lg font-semibold text-emerald-700">Approved</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center text-white font-bold">
                  {request.approver.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Approved by {request.approver.full_name}</p>
                  {request.approved_at && (
                    <p className="text-xs text-gray-500">
                      on {format(new Date(request.approved_at), 'MMMM dd, yyyy')}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Requester Info Card - Show when viewer is not the owner */}
        {!isOwner && request.user && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Requester Information</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white font-bold text-lg">
                  {request.user.full_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{request.user.full_name}</p>
                  <p className="text-sm text-gray-500">{request.user.email}</p>
                  {request.user.department && (
                    <p className="text-sm text-gray-500">{request.user.department}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Approval Actions Card - For approvers */}
        {canApprove && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Approval Actions</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Review the leave request and take action.
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to approve this leave request?')) {
                      approveMutation.mutate(undefined)
                    }
                  }}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50"
                >
                  {approveMutation.isPending ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Approving...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Approve
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={approveMutation.isPending || rejectMutation.isPending}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Actions Card */}
        {canCancel && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                {canCancelPending
                  ? 'You can cancel this leave request while it\'s still pending approval.'
                  : canCancelApproved && isOwner
                    ? 'You can cancel this approved leave since it hasn\'t started yet.'
                    : 'You can cancel this approved leave since it hasn\'t started yet.'}
              </p>
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to cancel this leave request?')) {
                    cancelMutation.mutate()
                  }
                }}
                disabled={cancelMutation.isPending}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelMutation.isPending ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Cancelling...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel Request
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Reject Leave Request</h3>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejecting this leave request..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                rows={4}
              />
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectReason('')
                }}
                className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!rejectReason.trim()) {
                    toast.error('Please provide a reason for rejection')
                    return
                  }
                  rejectMutation.mutate(rejectReason)
                  setShowRejectModal(false)
                  setRejectReason('')
                }}
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {rejectMutation.isPending ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Rejecting...
                  </>
                ) : (
                  'Reject Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveRequestDetailPage
